import { resolveReceiptLogoUrl } from '@/lib/receiptLogo';
// iter640 — Extracted from CashierPOSPage.jsx (was lines 40-171) to
// keep the page focused on rendering. These three helpers are pure,
// stateless functions used by the POS to (a) route an order to the
// right thermal printer based on category print_to routing, and (b)
// build the print-bridge payload that the local Bridge consumes.

// iter896 — Auto-classify common beverage category names to the BAR
// printer role when the admin hasn't set an explicit `print_to`. This
// lets "drinks go to the bar printer, food to the kitchen printer"
// work out-of-the-box without every tenant having to configure a
// category-by-category routing table. Users can still override by
// setting print_to explicitly on their category (which takes priority).
const BEVERAGE_CATEGORY_HINTS = [
  'beverage', 'beverages', 'drink', 'drinks', 'juice', 'juices',
  'smoothie', 'smoothies', 'cocktail', 'cocktails', 'mocktail',
  'coffee', 'tea', 'soda', 'sodas', 'soft drink', 'soft drinks',
  'water', 'wine', 'beer', 'spirits', 'bar', 'bar menu', 'shakes',
  'milkshake', 'lemonade', 'refresher', 'refreshers',
];

function _isBeverageCategoryName(name) {
  if (!name) return false;
  const n = String(name).toLowerCase().trim();
  return BEVERAGE_CATEGORY_HINTS.some(h => n === h || n.includes(h));
}

// Returns a clone of `order` containing only the items whose
// category routes to the given printer. `printerRole` is the
// printer's bare role ('kitchen' or 'bar'); `printerStation` is its
// optional station label. A category matches the printer when its
// print_to is either:
//   - the bare role ("kitchen" / "bar") AND the printer has NO
//     station (i.e., it's the catch-all for that role), OR
//   - "<role>:<station>" with a case-insensitive station match, OR
//   - the bare role AND there's no station-specific printer that
//     would otherwise catch this category (fallback to "any").
// Categories with print_to = "none" never match.
// iter896 — When `print_to` is not set, we auto-detect the beverage
// route from the category NAME (see BEVERAGE_CATEGORY_HINTS).
export function filterOrderForPrinter(order, printerRole, printerStation, categories, allPrinters) {
  if (!order) return order;
  const catById = {};
  const catByName = {};
  for (const c of (categories || [])) {
    if (c?.id) catById[c.id] = c;
    if (c?.name) catByName[String(c.name).toLowerCase()] = c;
  }
  const station = (printerStation || '').toLowerCase().trim();
  // Which stations of this role exist in this tenant? Used so a
  // bare "kitchen" category falls through to the "Kitchen (any)"
  // printer ONLY when no station-specific printer claims it.
  const stationPrintersOfRole = (allPrinters || [])
    .filter((p) => p.type === printerRole && (p.station || '').trim() !== '');

  // iter896 — Resolve the effective route for an item: explicit
  // `print_to` if set, otherwise auto-detect from category name
  // (drinks → bar, everything else → kitchen).
  const _routeFor = (it) => {
    const cat = (it?.category_id && catById[it.category_id])
      || (it?.category && catByName[String(it.category).toLowerCase()])
      || null;
    const explicit = cat?.print_to;
    if (explicit) return String(explicit).toLowerCase();
    const catName = cat?.name || it?.category || '';
    return _isBeverageCategoryName(catName) ? 'bar' : 'kitchen';
  };

  const matchesThisPrinter = (it) => {
    const route = _routeFor(it);
    if (route === 'none') return false;
    // "kitchen:xxx" / "bar:xxx"
    if (route.includes(':')) {
      const [r, s] = route.split(':');
      return r === printerRole && s.trim() === station;
    }
    // Bare role match.
    if (route !== printerRole) return false;
    // If THIS printer is the catch-all (no station), keep the item.
    if (!station) return true;
    // If THIS printer IS a station, only keep items that aren't
    // already routed to some specific station of the same role —
    // but since route is bare, items default to the catch-all not us.
    return false;
  };

  // Quick guard — when the user has only one station-printer of this
  // role, treat its station as the "catch-all" too. Otherwise a
  // bare "kitchen" route would never fire because no plain printer
  // exists.
  const onlyStationPrinter = (
    !station ? false : (
      stationPrintersOfRole.length === 1
      && stationPrintersOfRole[0].station?.toLowerCase().trim() === station
      && !(allPrinters || []).some((p) => p.type === printerRole && !p.station)
    )
  );

  // iter896 — Auto-fallback: when the tenant has NO printer of the
  // role that a drink item routes to (e.g. drinks map to 'bar' but
  // no bar printer exists), fall back to the kitchen printer so the
  // ticket still lands somewhere — better than silently dropping it.
  const hasPrinterOfRole = (role) => (allPrinters || []).some(p => p.type === role);
  const barMissing = printerRole === 'kitchen' && !hasPrinterOfRole('bar');

  const items = (order.items || []).filter((it) => {
    const route = _routeFor(it);
    if (route === 'none') return false;

    // iter896 — Kitchen-catches-all fallback when no bar printer
    // exists in the tenant's printer roster.
    if (barMissing && (route === 'bar' || route.startsWith('bar:')) && !station) {
      return true;
    }

    if (onlyStationPrinter) {
      // Fall through: bare role items also go to the single station printer.
      if (route === printerRole) return true;
      if (route.startsWith(`${printerRole}:`)) {
        return route.split(':')[1].trim() === station;
      }
      return false;
    }
    return matchesThisPrinter(it);
  });
  return { ...order, items };
}

// Legacy helper kept for the dashboard auto-fire path: route by role
// only. Internally delegates to the station-aware filter using the
// "catch-all" printer (no station).
export function filterOrderForRole(order, role, categories) {
  return filterOrderForPrinter(order, role, '', categories, []);
}

// Build a print-bridge payload from an order + cashier context. Kept
// outside the component so React lint doesn't flag the `new Date()` call
// as an impure render-time side-effect (it's only ever invoked from
// event handlers via printOrderViaBridge).
export function buildPrintPayloadHelper(order, opts, user, branchTax) {
  const role = (opts && opts.role) || 'receipt';
  const branch = (opts && opts.branch) || null;
  const branding = (opts && opts.branding) || null;
  const cur = order?.currency || branchTax?.currency_code || 'SAR';
  // iter630 — Resolve thermal logo to an absolute URL so the Bridge can fetch
  // it from any LAN computer. Falls back to no logo if not configured.
  // iter682 — If the tenant didn't upload a separate THERMAL (B/W)
  // logo, fall back to the regular branding.logo_url. Most users only
  // upload one logo image and expect it to show up on receipts too.
  // iter691 — Backfill legacy "/uploads/..." paths with "/api" so the
  // ingress routes them to the FastAPI StaticFiles mount instead of
  // hitting the frontend ingress and 404ing.
  const logoUrl = (() => {
    // iter921 — Shared resolver: Branch Logo → Global Logo. The same
    // resolved URL feeds ALL print paths (native USB/Android Bridge
    // bitmap, ePOS XML raster, browser dialog), so every renderer
    // prints the identical logo as the Preview and the POS.
    const raw = resolveReceiptLogoUrl(branding, branch);
    if (!raw) return '';
    // iter729 — Data URIs (iter728 browser-safe optimiser saves to
    // these) must be passed through UNCHANGED. The previous code only
    // matched `http(s)://` then fell into the path-resolution branch,
    // which prepended REACT_APP_BACKEND_URL to the data URI and
    // produced a nonsense URL like
    // `https://smartoerp.com/data:image/png;base64,...` — the image
    // loader couldn't fetch that, so the receipt printed without the
    // logo despite iter728's preview showing it correctly.
    if (raw.startsWith('data:')) return raw;
    if (/^https?:\/\//i.test(raw)) return raw;
    const base = process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? window.location.origin : '');
    // Legacy: stored as "/uploads/..." — needs the /api prefix.
    if (raw.startsWith('/uploads/')) return `${base}/api${raw}`;
    return `${base}${raw.startsWith('/') ? '' : '/'}${raw}`;
  })();
  // iter895 — Kitchen ticket header prefix so line cooks see at a
  // glance whether the food needs a bag (Takeaway), a table drop
  // (Dine-in) or the pickup shelf (Delivery). We fold the prefix
  // straight into `title` so both the native ESC/POS renderer AND
  // the browser-dialog HTML fallback pick it up without any
  // renderer-side changes.
  const rawOrderType = (order?.order_type || order?.dining_mode || '').toString().toLowerCase();
  const tableStr = (order?.table_number || '').toString().trim();
  let kitchenSuffix = '';
  if (rawOrderType === 'takeaway' || rawOrderType === 'take_away' || rawOrderType === 'take away') {
    kitchenSuffix = ' — 🛍️ TAKEAWAY';
  } else if (rawOrderType === 'delivery') {
    kitchenSuffix = ' — 🚴 DELIVERY';
  } else if (rawOrderType === 'dine_in' || rawOrderType === 'dine-in' || rawOrderType === 'dinein') {
    kitchenSuffix = tableStr ? ` — TABLE ${tableStr}` : ' — DINE-IN';
  }
  return {
    title: role === 'kitchen' ? `KITCHEN ORDER${kitchenSuffix}` : (branding?.invoice_title || 'RECEIPT'),
    company: branding?.company_name || user?.company_name || user?.tenant_name || 'SmartOERP',
    // iter959 — active-branch first: when a till is switched to act as another
    // branch, the receipt must show THAT branch, not the cashier's home branch.
    branch: branch?.name || user?.branch_name || 'Main Branch',
    vat: branding?.company_vat || user?.vat_number || branchTax?.vat_number || '',
    // Short address on the receipt: branch override → ZATCA street + city → first address line.
    address: branch?.company_address
      || [branding?.zatca_street, branding?.zatca_city].filter(Boolean).join(', ')
      || String(branding?.company_address || '').split('\n')[0],
    country_code: branch?.country_code || branding?.country_code || '',
    // B2B buyer (15-digit VAT no.) → receipt prints as a full Tax Invoice.
    buyer_name: order?.buyer?.name || order?.customer_name || '',
    buyer_vat: order?.buyer?.vat || order?.customer_vat || order?.buyer_vat || '',
    // Phase 2 signed QR when the order already carries one (reporting ON only).
    zatca_qr: order?.zatca?.qr || '',
    order_number: order?.order_number || order?.id || '-',
    // iter684 — Use ISO date so the receipt builder can format consistently
    // (compact / long). The previous `toLocaleString()` was locale-dependent
    // and overflowed 42-col paper.
    date: new Date(order?.created_at || Date.now()).toISOString(),
    opened_at: order?.opened_at ? new Date(order.opened_at).toISOString() : null,
    table: order?.table_number || '',
    // iter684 — Order type: Pick Up / Dine-in / Take Away / Delivery
    // (matches the sample receipt's "Pick Up" label).
    order_type: order?.order_type || order?.dining_mode || 'Pick Up',
    // iter684 — POS check number for table tracking (separate from order_number).
    check_number: order?.check_number || order?.check_no || order?.order_number || '',
    server: user?.name || '',
    items: (order?.items || []).map((it) => ({
      name: it.name || it.item_name || 'Item',
      qty: it.quantity || 1,
      subtotal: it.subtotal || (it.quantity || 1) * (it.unit_price || 0),
      modifiers: it.modifiers || [],
      note: it.note || '',
    })),
    subtotal: order?.subtotal || 0,
    discount: order?.discount || 0,
    tax: order?.tax || 0,
    tax_pct: order?.tax_pct || branchTax?.vat_rate || 15,
    total: order?.total || 0,
    currency: cur,
    payment_method: order?.payment_method || '',
    // iter684 — Optional human-friendly payment label (e.g. "Bilad")
    // that overrides the all-caps method enum on the receipt.
    payment_label: order?.payment_label || '',
    // iter628 — Per-branch thermal receipt header/footer (admin-configurable).
    // iter684 — Cascade: branch-level value first, then tenant-wide default.
    header: branch?.receipt_header || branding?.receipt_header_text || '',
    tenant_header_text: branding?.receipt_header_text || '',
    footer: branch?.receipt_footer || branding?.footer_text || 'Thank you!',
    // iter630 — Tenant-wide thermal logo URL (Bridge fetches + downscales).
    logo_url: logoUrl,
    // iter767 — Logo print size from branding.thermal_logo_size
    // (Small / Medium / Large) → scale factor applied by the raster renderer.
    logo_scale: ({ small: 0.75, medium: 1, large: 1.2 }[branding?.thermal_logo_size] ?? 1),
    // iter684 — Pass receipt section toggles + ZATCA seller through to
    // the builder so the operator can switch sections on/off from
    // Settings → Receipt without code changes.
    options: branding?.receipt_options || {},
    zatca_seller_name: branding?.zatca_seller_name || branding?.company_name || '',
    // iter730 — Receipt language settings. The builder picks labels
    // from these (main / localized / both side-by-side).
    print_language: branding?.print_language || 'main',
    main_language: branding?.main_language || 'en',
    localized_language: branding?.localized_language || 'ar',
    // iter917 — Tailor sale metadata (measurements, ready date, …) so the
    // receipt renderers can print the MEASUREMENTS / تعديل section.
    metadata: order?.metadata || null,
  };
}
